import { ArrowRight, BookOpen, ChevronDown, HeartHandshake, Sparkles, Sprout } from "lucide-react";
import { Link } from "react-router-dom";
import { StarField } from "../components/sky/StarField";

const pillars = [
  [BookOpen, "Learn", "Build knowledge through Foundation learning and experiences."],
  [
    HeartHandshake,
    "Serve",
    "Put your time and strengths toward programs, events, and community needs.",
  ],
  [Sprout, "Grow", "Reflect on your experiences and develop skills you can carry forward."],
  [Sparkles, "Shine", "Ignite Stars that represent meaningful milestones throughout your journey."],
] as const;

const journey = [
  ["01", "Join", "Become part of the Guiding Stars community."],
  ["02", "Discover", "Explore Foundation learning, programs, events, and ways to serve."],
  ["03", "Contribute", "Use your time, knowledge, skills, and compassion to make a difference."],
  ["04", "Ignite", "Meaningful accomplishments illuminate Stars within your personal Atlas."],
  [
    "05",
    "Keep going",
    "As your involvement grows, your Atlas becomes a reflection of your journey with the Foundation.",
  ],
] as const;

export function HomePage() {
  return (
    <div className="public-home">
      <section className="hero" aria-labelledby="hero-title">
        <div className="hero-film-still" aria-hidden="true" />
        <div className="aurora" aria-hidden="true" />
        <div className="site-shell hero-layout">
          <div className="hero-copy">
            <p className="eyebrow">Guiding Stars</p>
            <h1 id="hero-title">Be the light that helps someone find their way.</h1>
            <p className="hero-intro">
              The Guided Pathway Foundation’s community for learning, service, and meaningful
              recognition.
            </p>
            <div className="hero-actions">
              <Link className="button button-gold" to="/get-involved">
                Become a Guiding Star <ArrowRight size={17} aria-hidden="true" />
              </Link>
              <Link className="button button-ghost" to="/sign-in">
                Enter My Atlas <ArrowRight size={17} aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
        <a
          className="scroll-cue"
          href="#about-guiding-stars"
          aria-label="Learn about Guiding Stars"
        >
          <span>Discover</span>
          <ChevronDown size={18} />
        </a>
      </section>

      <section
        id="about-guiding-stars"
        className="program-intro section-space"
        aria-labelledby="program-title"
      >
        <div className="site-shell">
          <div className="public-section-heading">
            <p className="eyebrow">The Guiding Stars Program</p>
            <h2 id="program-title">Turn what you bring into meaningful community impact.</h2>
            <div className="public-section-copy">
              <p>
                Learn with the Foundation, find ways to serve, and see how your individual
                contribution becomes part of a stronger, more connected community.
              </p>
            </div>
          </div>
          <div className="program-pillars">
            {pillars.map(([Icon, title, copy], index) => (
              <details key={title}>
                <summary>
                  <span>0{index + 1}</span>
                  <Icon aria-hidden="true" />
                  <h3>{title}</h3>
                  <ChevronDown className="expand-cue" aria-hidden="true" />
                </summary>
                <p>{copy}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section
        id="how-it-works"
        className="journey-section section-space"
        aria-labelledby="journey-title"
      >
        <div className="site-shell">
          <div className="public-section-heading centered">
            <p className="eyebrow">Your Guiding Stars Journey</p>
            <h2 id="journey-title">Your journey begins with one Star.</h2>
          </div>
          <ol className="journey-steps">
            {journey.map(([number, title, copy]) => (
              <li key={number}>
                <details>
                  <summary>
                    <span>{number}</span>
                    <h3>{title}</h3>
                    <ChevronDown className="expand-cue" aria-hidden="true" />
                  </summary>
                  <p>{copy}</p>
                </details>
              </li>
            ))}
          </ol>
          <div className="journey-summary">
            <p>
              Your private Atlas brings the experience together—learning, opportunities, service,
              reflection, and the Stars you ignite along the way.
            </p>
            <Link className="text-link" to="/about">
              See how the Atlas works <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <section className="closing-callout">
        <StarField />
        <div className="site-shell closing-content">
          <p className="eyebrow">Ready to begin?</p>
          <h2>Every community needs people willing to shine.</h2>
          <p>
            Bring your time, strengths, and willingness to help. We’ll help you find where they can
            make an impact.
          </p>
          <Link className="button button-gold" to="/get-involved">
            Become a Guiding Star <ArrowRight size={17} />
          </Link>
          <p className="closing-member-path">
            Already part of the Foundation?{" "}
            <Link to="/sign-in">
              Enter My Atlas <ArrowRight size={14} />
            </Link>
          </p>
        </div>
      </section>
    </div>
  );
}
